import { useCallback, useEffect, useState, useMemo } from 'react';
import { Drawer, Button } from 'antd';
import UploadImages from './UploadImages';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import {
  updateOrganizationLogoAPI,
  uploadOrganizationLogoAction,
} from 'src/modules/SuperAdmin/store/actions/organization.action';
import { logoUploadSuccessSlice } from 'src/modules/SuperAdmin/store/slices/organization.slice';
import { useNavigate } from 'react-router-dom';

const LogoManagementDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );

  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { logoUploadLoading, logoUploadSuccess } = useSelector(
    (state) => state?.organizationState
  );

  // Memoize initial values to prevent recreation on every render
  const initialValues = useMemo(
    () => ({
      logoUUID: uuidv4(),
      org_name: settingsOrgValue?.org_name || '',
      logo_url: settingsOrgValue?.org_logo_url || '',
    }),
    [settingsOrgValue?.org_name, settingsOrgValue?.org_logo_url]
  );

  const [orgData, setOrgData] = useState(initialValues);
  const [selectedFile, setSelectedFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(
    settingsOrgValue?.org_logo_url || null
  );

  // Update logoUrl with a stable function reference
  const updateLogoUrl = useCallback((url) => {
    if (url !== undefined) {
      // Only update if url is provided
      setOrgData((prevOrgData) => ({
        ...prevOrgData,
        logo_url: url,
        logo_file: null,
        logo_type: null,
      }));
    }
  }, []);

  const handleBlobSelect = useCallback((blob) => {
    if (!blob) return;

    setSelectedFile(blob);
    setOrgData((prevOrgData) => ({
      ...prevOrgData,
      logo_url: null,
      logo_file: {
        ...blob,
        size: blob.size,
        type: blob.type,
      },
    }));
  }, []);

  // Set logoUrl once when organization data changes
  useEffect(() => {
    if (settingsOrgValue?.org_logo_url) {
      setLogoUrl(settingsOrgValue?.org_logo_url);
    }
  }, [settingsOrgValue?.org_logo_url]);

  // Initialize orgData once when initial values change
  useEffect(() => {
    setOrgData(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (logoUploadSuccess === true && !logoUploadLoading && isOpen) {
      dispatch(logoUploadSuccessSlice(null));
      onClose();
    }
  }, [logoUploadSuccess, logoUploadLoading, isOpen, onClose]);

  const handleSave = async () => {
    try {
      const updatedOrgData = {
        ...orgData,
        logo_url: orgData?.logo_url,
        logo_file: '',
        logo_type: '',
      };

      if (selectedFile) {
        await dispatch(
          uploadOrganizationLogoAction(
            {
              file_name: `${orgData.logoUUID}.${selectedFile?.ext}`,
              logo: selectedFile,
            },
            navigate,
            updatedOrgData,
            'isEditPage',
            settingsOrgValue?.org_id || currentPractitioner?.org_id,
            true
          )
        );
      } else {
        await dispatch(
          updateOrganizationLogoAPI(
            settingsOrgValue?.org_id || currentPractitioner?.org_id,
            orgData?.logo_url
          )
        );
      }
    } catch (error) {
      console.error('Error saving logo:', error);
    }
  };

  return (
    <Drawer
      title="Change Organization Logo"
      placement="right"
      width={500}
      onClose={onClose}
      open={isOpen}
      footer={
        <div className="flex justify-end items-center gap-2">
          <Button onClick={onClose} disabled={logoUploadLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            className="ih-button-primary"
            loading={logoUploadLoading}
            disabled={logoUploadLoading}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <div className="container mx-auto py-2">
        <UploadImages
          isEditPage={true}
          updateLogoUrl={updateLogoUrl}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          handleBlobSelect={handleBlobSelect}
          logoUrl={logoUrl}
          setLogoUrl={setLogoUrl}
          setOrgData={setOrgData}
          orgData={orgData}
          fromAddPage={false}
        />
      </div>
    </Drawer>
  );
};

export default LogoManagementDrawer;
